import { useEffect, useRef, useState } from 'react';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import { axiosInstance } from '../../../utility/apiInstance';
import { useRole } from '../../MangeContextProvide';
import { ICustomField } from './types/CustomFieldTypes';
import CustomFieldsPanel from './CustomFieldsPanel';
import { useShipmentEdit } from './shipmentEdit/UseShipmentEditHook';
import { isTrackingNumberAssigned } from './shipmentEdit/Shipmenteditservice';
import { shipmentEditStyles as es } from './shipmentEdit/Shipmenteditstyles';
import { handleAWBNumberInput, handleOceanNumberInput } from '../trackingInputHandle';
import TrackingWarningModal from './shipmentEdit/Trackingwarningmodal';
import { useDashboard } from '../DashboardContext/DashboardContext';


// ── Card base styles ──────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  card: {
    background: '#ffffff',
    borderRadius: '14px',
    border: '1px solid #e8e8e8',
    boxShadow: '0 2px 16px rgba(0,0,0,0.07)',
    display: 'flex',
    alignItems: 'stretch',
    width: '100%',
    position: 'relative',
    overflow: 'visible',
    transition: 'border-color 0.2s ease',
  },
  logoBlock: {
    flexShrink: 0,
    width: '96px',
    borderRadius: '12px 0 0 12px',
    background: '#fafafa',
    borderRight: '1px solid #efefef',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '10px',
  },
  logoImg: { width: '64px', height: '64px', objectFit: 'contain' as const },
  infoRow: { flex: 1, display: 'flex', alignItems: 'stretch', minWidth: 0 },
  infoCell: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px 12px',
    borderRight: '1px solid #f0f0f0',
    gap: '5px',
    minWidth: 0,
    overflow: 'hidden',
  },
  infoCellLast: { borderRight: 'none' },
  val: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#1a1a1a',
    letterSpacing: '0.01em',
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
  },
  lbl: {
    fontSize: '10px',
    fontWeight: 500,
    color: '#9ca3af',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
  },
  controls: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '0 16px',
    borderLeft: '1px solid #f0f0f0',
  },
  popoverInput: {
    flex: 1,
    border: '1px solid #e8e8e8',
    borderRadius: '8px',
    padding: '8px 12px',
    fontSize: '12px',
    color: '#555',
    background: '#fafafa',
    outline: 'none',
    minWidth: 0,
    fontFamily: 'inherit',
  },
  copyBtn: {
    background: '#1a1a1a',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '8px 14px',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
    fontFamily: 'inherit',
  },
  expandPanel: {
    background: '#ffffff',
    borderRadius: '0 0 14px 14px',
    border: '1px solid #e8e8e8',
    borderTop: 'none',
    boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
    width: '100%',
    marginBottom: '5px',
    overflow: 'hidden',
  },
};

// ── Small reusable label ──────────────────────────────────────────────────────

const FieldLabel = ({ text }: { text: string }) => (
  <span style={styles.lbl}>{text}</span>
);

// ── Component ─────────────────────────────────────────────────────────────────

function TabActionLogo() {
  const [milestoneGrouping, setMilestoneGrouping] = useState('');
  const [airlineLogo, setAirlineLogo] = useState('');
  const [publicUrl, setPublicUrl] = useState('');
  const [showPopover, setShowPopover] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [customFields, setCustomFields] = useState<ICustomField[]>([]);
  const [openData, setOpenData] = useState(false);

  // ── Source of truth: selectedItem from DashboardContext ──────────────────
  const { selectedItem, setSelectedItem } = useDashboard();

  // Guard: nothing to render if no item is selected
  if (!selectedItem) return null;

  const [displayedMawb, setDisplayedMawb] = useState(selectedItem.mawb);

  const containerRef = useRef<HTMLDivElement>(null);
  const { trackingData, setTrackingData } = useRole() as any;
  const navigate = useNavigate();

  // ── Derived values — prefer live trackingData, fall back to selectedItem ──
  const origin = trackingData?.origin ?? selectedItem.origin ?? '';
  const destination = trackingData?.destination ?? selectedItem.destination ?? '';
  const pkts = trackingData?.pkts ?? selectedItem.pkts ?? 0;
  const weight = trackingData?.weight ?? selectedItem.weight ?? 0;
  const uniqueValue = trackingData?.uniqueValue ?? selectedItem.uniqueValue ?? selectedItem.mawb;
  const airline = trackingData?.airline ?? selectedItem.airline ?? '—';

  const isOcean = selectedItem.for === 'ocean';

  // ── fetchTrackingData: accepts an override to avoid stale-closure issues ──
  const fetchTrackingData = async (overrideTracking?: string) => {
    try {
      const { data } = await axiosInstance.post('/api/v1/tracking/data', {
        type: selectedItem.for,
        trackingNumber: overrideTracking ?? displayedMawb,
      });
      setTrackingData(data.data);
      setSelectedItem({
        ...selectedItem,
        ...data.data

      })
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Something went wrong!');
    }
  };

  // ── Edit hook ─────────────────────────────────────────────────────────────
  const {
    state: editState,
    startEditing,
    cancelEditing,
    updateField,
    updateNewTrackingNumber,
    dismissTrackingWarning,
    confirmTrackingUpdate,
    saveChanges,
  } = useShipmentEdit({
    mawb: displayedMawb,
    type: selectedItem.for,
    uniqueValue,
    initialOrigin: origin,
    initialDestination: destination,
    initialPkts: pkts,
    initialWeight: weight,
    isTrackingSet: isTrackingNumberAssigned(selectedItem.awbLocked),
    onSaved: (updatedFields) => {
      // Capture the latest tracking number BEFORE any state setter runs
      const latestTracking = updatedFields.trackingNumber ?? displayedMawb;

      // ── Sync displayedMawb local state ────────────────────────────────
      if (updatedFields.trackingNumber) {
        setDisplayedMawb(updatedFields.trackingNumber);
      }

      // ── Sync trackingData role context ────────────────────────────────
      setTrackingData((prev: any) => ({
        ...prev,
        origin: updatedFields.origin,
        destination: updatedFields.destination,
        pkts: updatedFields.pkts,
        weight: updatedFields.weight,
      }));

      // ── Sync selectedItem in DashboardContext so all consumers see the
      //    latest values immediately without a full re-fetch ─────────────
      setSelectedItem({
        ...selectedItem,
        ...(updatedFields.trackingNumber && { mawb: updatedFields.trackingNumber }),
        ...(updatedFields.origin !== undefined && { origin: updatedFields.origin }),
        ...(updatedFields.destination !== undefined && { destination: updatedFields.destination }),
        ...(updatedFields.pkts !== undefined && { pkts: Number(updatedFields.pkts) }),
        ...(updatedFields.weight !== undefined && { weight: Number(updatedFields.weight) }),
      });

      // ── Re-fetch with the latest tracking number (updates airline etc.) ─
      fetchTrackingData(latestTracking);
    },
  });

  const {
    isEditing, isSaving, isTrackingNumberLocked,
    showTrackingWarning, fields, newTrackingNumber, errors,
  } = editState;

  // ── Re-sync hook fields when trackingData loads after mount ───────────────
  useEffect(() => {
    if (!isEditing) {
      updateField('origin', origin);
      updateField('destination', destination);
      updateField('pkts', String(pkts));
      updateField('weight', String(weight));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackingData]);

  // ── Tracking number input — routes to air or ocean handler ───────────────
  const handleTrackingNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted =
      selectedItem.for === 'air'
        ? handleAWBNumberInput(e)
        : handleOceanNumberInput(e);
    updateNewTrackingNumber(formatted);
  };

  // ── Fetchers ──────────────────────────────────────────────────────────────
  const fetchMilestoneGrouping = async () => {
    if (!selectedItem.groupMilestone) return;
    try {
      const { data } = await axiosInstance.post('/api/v1/groupingMileStone/grouping', {
        type: selectedItem.for, id: selectedItem.groupMilestone,
      });
      setMilestoneGrouping(data?.data?.groupName || '');
    } catch { setMilestoneGrouping(''); }
  };

  const fetchCustomFieldOfTrackingNumber = async () => {
    try {
      const { data } = await axiosInstance.get(
        '/api/v1/custom-field/custom-field-trakcing-number',
        { params: { trackingNumber: selectedItem.mawb, type: selectedItem.for } }
      );
      setCustomFields(data?.data || []);
    } catch { setCustomFields([]); }
  };

  const fetchAirlineLogo = async () => {
    try {
      setImageLoading(true);
      const payload: { code?: string } = {};
      if (selectedItem.for === 'air') {
        const [prefix] = selectedItem.mawb.split('-');
        payload.code = prefix.replace(/^0+/, '');
      }
      const { data } = await axiosInstance.post('/api/v1/airline', payload);
      setAirlineLogo(data?.data?.logoLocation || '');
    } catch { setAirlineLogo(''); }
    finally { setImageLoading(false); }
  };

  useEffect(() => {
    fetchMilestoneGrouping();
    fetchAirlineLogo();
    fetchTrackingData();
    fetchCustomFieldOfTrackingNumber();
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node))
        setShowPopover(false);
    };
    if (showPopover) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showPopover]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleShareClick = async () => {
    if (showPopover) { setShowPopover(false); return; }
    setPublicUrl('');
    try {
      const apiUrl = selectedItem.for === 'ocean'
        ? '/api/v1/ocean/generate-public-link'
        : '/api/v1/awb-tracked-list/generate-public-link';
      const { data } = await axiosInstance.get(`${apiUrl}/${displayedMawb}`);
      setPublicUrl(data?.data?.publicUrl || '');
      setShowPopover(true);
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Something went wrong!');
    }
  };

  const handleRedirectTrackingPage = () => {
    const url = selectedItem.for === 'ocean'
      ? `/oceanTrackingDetail?trackingId=${displayedMawb}`
      : `/trackingDetail?trackingId=${displayedMawb}`;
    navigate(url);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(publicUrl);
    toast.success('URL copied successfully!');
    setShowPopover(false);
  };

  const fallbackSrc = selectedItem.for === 'air'
    ? '/images/air_yellow.svg'
    : '/images/ocean_yellow.svg';

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '5px' }}>

      {/* Warning modal — rendered as overlay portal */}
      {showTrackingWarning && (
        <TrackingWarningModal
          newTracking={newTrackingNumber}
          onConfirm={confirmTrackingUpdate}
          onCancel={dismissTrackingWarning}
          isSaving={isSaving}
        />
      )}

      {/* ── Card ─────────────────────────────────────────────────────────── */}
      <div
        className="detailMain"
        style={{
          ...styles.card,
          borderRadius: openData ? '14px 14px 0 0' : '14px',
          borderBottom: openData ? 'none' : '1px solid #e8e8e8',
          border: isEditing ? '1.5px solid #F8B71F' : '1px solid #e8e8e8',
        }}
      >
        <div className="detailOuter" style={{ display: 'flex', alignItems: 'stretch', width: '100%' }}>

          {/* Logo */}
          <div style={styles.logoBlock}>
            {imageLoading ? (
              <CircularProgress size={28} sx={{ color: '#F8B71F' }} />
            ) : (
              <img
                style={styles.logoImg}
                src={airlineLogo || fallbackSrc}
                alt="carrier-logo"
                onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = fallbackSrc;
                }}
              />
            )}
          </div>

          {/* Info cells */}
          <div style={styles.infoRow}>

            {/* ① Added Date */}
            <div style={styles.infoCell}>
              <span style={styles.val}>{selectedItem.createdAt}</span>
              <FieldLabel text="Added Date" />
            </div>

            {/* ② Tracking number */}
            <div className="arrowMiddleModal" style={styles.infoCell}>
              {isEditing && !isTrackingNumberLocked ? (
                <div style={es.trackingEditRow}>
                  <input
                    style={{
                      ...es.trackingInput,
                      ...(errors.trackingNumber ? es.inlineInputError : {}),
                    }}
                    type="text"
                    value={newTrackingNumber}
                    placeholder={displayedMawb}
                    onChange={handleTrackingNumberChange}
                  />
                  {errors.trackingNumber ? (
                    <span style={es.errorText}>{errors.trackingNumber}</span>
                  ) : (
                    <span style={{ fontSize: '9px', color: '#d97706', fontWeight: 500 }}>
                      ⚠ One-time update
                    </span>
                  )}
                </div>
              ) : isEditing && isTrackingNumberLocked ? (
                <div style={es.trackingEditRow}>
                  <span style={styles.val}>{displayedMawb}</span>
                </div>
              ) : (
                <span style={styles.val}>{displayedMawb}</span>
              )}

              {/* airline — updates from trackingData after save */}
              <FieldLabel text={airline} />

              <div className="lastBtnArrow">
                <button
                  type="button"
                  className="btn"
                  onClick={() => setOpenData((prev) => !prev)}
                >
                  <ExpandMoreIcon className={openData ? 'arrowIcon active' : 'arrowIcon'} />
                </button>
              </div>
            </div>

            {/* ③ Origin → Destination */}
            <div style={styles.infoCell}>
              {isEditing ? (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}>
                    <input
                      style={{ ...es.inlineInput, width: '72px', ...(errors.origin ? es.inlineInputError : {}) }}
                      type="text"
                      value={fields.origin}
                      placeholder="Origin"
                      maxLength={10}
                      onChange={(e) => {
                        const cleaned = e.target.value.replace(/[^a-zA-Z\s]/g, '');
                        updateField('origin', cleaned);
                      }}
                    />
                    {errors.origin
                      ? <span style={es.errorText}>{errors.origin}</span>
                      : <span style={styles.lbl}>Origin</span>
                    }
                  </div>
                  <span style={{ color: '#9ca3af', fontWeight: 700, paddingTop: '7px', fontSize: '12px' }}>→</span>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}>
                    <input
                      style={{ ...es.inlineInput, width: '72px', ...(errors.destination ? es.inlineInputError : {}) }}
                      type="text"
                      value={fields.destination}
                      placeholder="Dest"
                      maxLength={10}
                      onChange={(e) => {
                        const cleaned = e.target.value.replace(/[^a-zA-Z\s]/g, '');
                        updateField('destination', cleaned);
                      }}
                    />
                    {errors.destination
                      ? <span style={es.errorText}>{errors.destination}</span>
                      : <span style={styles.lbl}>Dest</span>
                    }
                  </div>
                </div>
              ) : (
                <span style={styles.val}>{origin || '—'} → {destination || '—'}</span>
              )}
            </div>

            {/* ④ Pkts · Weight */}
            <div style={{ ...styles.infoCell, ...styles.infoCellLast }}>
              {isEditing ? (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>

                  {/* Pkts */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}>
                    <input
                      style={{
                        ...es.inlineInput,
                        width: '100%',
                        ...(errors.pkts ? es.inlineInputError : {}),
                        ...(isOcean && { opacity: 0.45, cursor: 'not-allowed', background: '#f3f4f6' }),
                      }}
                      type="number"
                      min={0}
                      value={fields.pkts}
                      placeholder="0"
                      disabled={isOcean}
                      onKeyDown={(e) => ['-', '+', 'e', 'E'].includes(e.key) && e.preventDefault()}
                      onChange={(e) => {
                        const val = Math.max(0, Number(e.target.value));
                        updateField('pkts', String(val));
                      }}
                    />
                    {errors.pkts
                      ? <span style={es.errorText}>{errors.pkts}</span>
                      : <span style={styles.lbl}>Pkts</span>
                    }
                  </div>

                  <span style={{ color: '#9ca3af', fontWeight: 700, paddingTop: '7px', fontSize: '12px' }}>·</span>

                  {/* Weight */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}>
                    <input
                      style={{
                        ...es.inlineInput,
                        width: '100%',
                        ...(errors.weight ? es.inlineInputError : {}),
                        ...(isOcean && { opacity: 0.45, cursor: 'not-allowed', background: '#f3f4f6' }),
                      }}
                      type="number"
                      min={0}
                      value={fields.weight}
                      placeholder="0"
                      disabled={isOcean}
                      onKeyDown={(e) => ['-', '+', 'e', 'E'].includes(e.key) && e.preventDefault()}
                      onChange={(e) => {
                        const val = Math.max(0, Number(e.target.value));
                        updateField('weight', String(val));
                      }}
                    />
                    {errors.weight
                      ? <span style={es.errorText}>{errors.weight}</span>
                      : <span style={styles.lbl}>Kgs</span>
                    }
                  </div>

                </div>
              ) : (
                <span style={styles.val}>{pkts} Pkts · {weight} Kgs</span>
              )}
            </div>

          </div>{/* end infoRow */}

          {/* Controls */}
          <div style={styles.controls}>
            {isEditing ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '90px' }}>
                <button
                  style={es.saveBtn}
                  onClick={saveChanges}
                  disabled={isSaving}
                  type="button"
                >
                  {isSaving ? (
                    <><CircularProgress size={11} sx={{ color: '#1a1a1a' }} /> Saving…</>
                  ) : (
                    <><CheckIcon style={{ fontSize: 14 }} /> Save</>
                  )}
                </button>
                <button
                  style={es.cancelBtn}
                  onClick={cancelEditing}
                  disabled={isSaving}
                  type="button"
                >
                  <CloseIcon style={{ fontSize: 13 }} /> Cancel
                </button>
              </div>
            ) : (
              <div className="sendShareBtnOuter" ref={containerRef}>

                {/* Edit button */}
                <button
                  type="button"
                  style={{
                    ...es.editIconBtn,
                    marginRight: '4px',
                    ...(selectedItem.isEditDisabled && {
                      opacity: 0.4,
                      cursor: 'not-allowed',
                      pointerEvents: 'none',
                    }),
                  }}
                  title="Edit shipment details"
                  disabled={selectedItem.isEditDisabled}
                  onClick={startEditing}
                >
                  <EditIcon style={{ fontSize: 17 }} />
                </button>

                <button className="sendBtn" onClick={handleRedirectTrackingPage} type="button">
                  Track
                </button>
                <button
                  className={`shareBtn ${showPopover ? 'active' : ''}`}
                  onClick={handleShareClick}
                  title="Share public link"
                >
                  <ArrowDropDownIcon className={showPopover ? 'rotateIcon active' : 'rotateIcon'} />
                </button>
                {showPopover && (
                  <div className="copyDropdown">
                    <input style={styles.popoverInput} type="text" value={publicUrl} readOnly />
                    <button style={styles.copyBtn} onClick={handleCopy}>Copy URL</button>
                  </div>
                )}
              </div>
            )}
          </div>

        </div>
      </div>{/* end card */}

      {/* Expand panel */}
      {openData && (
        <div style={styles.expandPanel}>
          <CustomFieldsPanel
            fields={customFields}
            onSaved={(updated) => setCustomFields(updated)}
            contactOrganisation={selectedItem?.contactOrganisation || ''}
            trackingNumber={displayedMawb}
            type={selectedItem.for}
          />
        </div>
      )}

    </div>
  );
}

export default TabActionLogo;